import { useRef, useState } from "react";
import { ReactCropperElement } from "react-cropper";

import { useTranslator } from "@eo-locale/react";
import { FiEdit3, FiImage, FiTrash } from "react-icons/fi";

import {
  AspectRatio,
  Box,
  Input,
  Dialog,
  Drawer,
  Portal,
  useDisclosure,
  useMediaQuery
} from "@chakra-ui/react";

import { asSlotComponent } from "../internal/v3-slot";
import { Button, ButtonGroup } from "../button/button";
import { IconButton } from "../button/icon-button";
import { Image } from "../image/image";

import { Controls } from "./components/controls";
import { Editor } from "./components/editor";
import { Footer } from "./components/footer";
import { Header } from "./components/header";
import { CropperContext } from "./context";

const DialogBackdrop = asSlotComponent(Dialog.Backdrop);
const DialogPositioner = asSlotComponent(Dialog.Positioner);
const DialogContent = asSlotComponent(Dialog.Content);
const DialogHeader = asSlotComponent(Dialog.Header);
const DialogBody = asSlotComponent(Dialog.Body);
const DialogFooter = asSlotComponent(Dialog.Footer);
const DrawerBackdrop = asSlotComponent(Drawer.Backdrop);
const DrawerPositioner = asSlotComponent(Drawer.Positioner);
const DrawerContent = asSlotComponent(Drawer.Content);
const DrawerHeader = asSlotComponent(Drawer.Header);
const DrawerBody = asSlotComponent(Drawer.Body);
const DrawerFooter = asSlotComponent(Drawer.Footer);

interface CropperProps {
  disabled: boolean;
  onUpdate: () => void;
  onReplace: () => void;
  onDelete: () => void;
  onCropCancel: () => void;
  keepOriginalImageToBeCropped: boolean;
  onCropComplete: (imageUrl: string) => void;
  renderAsButton?: boolean;
  getFileDetails?: (file: any) => void;
  existingImageUrl?: string;
}

function Cropper({
  disabled,
  onUpdate,
  onReplace,
  onDelete,
  onCropCancel,
  onCropComplete,
  keepOriginalImageToBeCropped = true,
  renderAsButton,
  getFileDetails,
  existingImageUrl
}: Partial<CropperProps>) {
  const { translate } = useTranslator();

  const [previewImage, setPreviewImage] = useState(existingImageUrl || "");
  const [originalImage, setOriginalImage] = useState("");
  const [isCropActive, setIsCropActive] = useState(false);
  const [imageToBeCropped, setImageToBeCropped] = useState(
    existingImageUrl || ""
  );

  const cropStatus = existingImageUrl ? "enabled" : "disabled";
  const [status, setStatus] = useState<"disabled" | "enabled">(cropStatus);
  const { open, onOpen, onClose } = useDisclosure();

  const cropperRef = useRef<ReactCropperElement>(null);
  const inputFileNode = useRef<HTMLInputElement>(null);

  const [isDesktop] = useMediaQuery(["(min-width: 768px)"]);

  const destroyCrop = () => {
    if (originalImage) {
      setImageToBeCropped(originalImage);
    } else {
      cropperRef.current?.cropper.destroy();
      if (!existingImageUrl) setImageToBeCropped("");
    }

    if (onCropCancel) onCropCancel();

    setIsCropActive(false);
    onClose();
  };

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      destroyCrop();
    }
  };

  const submitCrop = () => {
    setStatus("disabled");

    const cropedCanvas = cropperRef.current?.cropper.getCroppedCanvas();
    const croppedImageUrl = cropedCanvas?.toDataURL("image/jpeg") || "";

    const originalCanvas = cropperRef.current?.cropper
      .clear()
      .reset()
      .getCroppedCanvas();
    const originalImageUrl = originalCanvas?.toDataURL("image/jpeg") || "";

    setOriginalImage(originalImageUrl);

    if (keepOriginalImageToBeCropped) setImageToBeCropped(originalImageUrl);
    else setImageToBeCropped(croppedImageUrl);

    setPreviewImage(croppedImageUrl);

    if (onCropComplete) onCropComplete(croppedImageUrl);

    cropperRef.current?.cropper.destroy();

    setStatus("enabled");
    setIsCropActive(false);
    onClose();
  };

  return (
    <CropperContext.Provider
      value={{
        cropperRef,
        destroyCrop,
        status,
        setStatus,
        submitCrop,
        imageToBeCropped,
        setImageToBeCropped,
        isCropActive,
        setIsCropActive
      }}
    >
      <Box
        display={previewImage ? "block" : "none"}
        w="full"
        maxW="27rem"
        data-testid="imageEditor-container"
      >
        <AspectRatio ratio={16 / 9}>
          <Image alt="" src={previewImage} data-testid="imageEditor-preview" />
        </AspectRatio>
        <ButtonGroup
          mt={2}
          gap={1}
          size="xs"
          justifyContent="space-between"
          display={{ base: "flex", lg: "inline-flex" }}
          disabled={disabled || status === "disabled"}
        >
          <Button
            variant="tertiary"
            leftIcon={<FiEdit3 />}
            backgroundColor="inherit"
            onClick={() => {
              if (onUpdate) onUpdate();
              onOpen();
            }}
            data-testid="imageEditor-update"
          >
            {translate("common.update")}
          </Button>
          <Button
            variant="tertiary"
            leftIcon={<FiImage />}
            backgroundColor="inherit"
            onClick={() => {
              if (onReplace) onReplace();
              inputFileNode.current?.click();
            }}
            data-testid="imageEditor-replace"
          >
            {translate("common.replace")}
          </Button>
          <Button
            variant="tertiary"
            leftIcon={<FiTrash />}
            backgroundColor="inherit"
            onClick={() => {
              setImageToBeCropped("");
              setOriginalImage("");
              setPreviewImage("");
              if (onDelete) onDelete();
            }}
            data-testid="imageEditor-delete"
          >
            {translate("common.delete")}
          </Button>
        </ButtonGroup>
      </Box>
      <Box display={previewImage ? "none" : "initial"}>
        {renderAsButton ? (
          <Button
            w="full"
            leftIcon={<FiImage />}
            variant="secondary"
            justifyContent="center"
            onClick={() => inputFileNode.current?.click()}
          >
            {translate("common.addAnImage")}
          </Button>
        ) : (
          <IconButton
            round
            variant="secondary"
            icon={<FiImage />}
            disabled={disabled}
            w={{ base: 10, md: 8 }}
            h={{ base: 10, md: 8 }}
            fontSize={{ base: "md", md: "sm" }}
            aria-label={translate("common.upload")}
            onClick={() => inputFileNode.current?.click()}
          />
        )}
        <Input
          type="file"
          display="none"
          ref={inputFileNode}
          onChange={(e) => {
            const file = e.target.files?.[0];

            if (file && /^image\//.test(file.type)) {
              const reader = new window.FileReader();
              reader.addEventListener("load", () => {
                setImageToBeCropped(reader.result?.toString() || "");
              });
              reader.readAsDataURL(file);
              getFileDetails?.(file);
              e.target.value = "";

              setImageToBeCropped("");
              setOriginalImage("");
              onOpen();
            }
          }}
          accept=".jpg,.jpeg,.gif,.png,.bmp,.webp"
        />
        {isDesktop ? (
          <Dialog.Root
            open={open}
            onOpenChange={handleOpenChange}
            placement="center"
          >
            <Portal>
              <DialogBackdrop />
              <DialogPositioner>
                <DialogContent>
                  <DialogHeader>
                    <Header />
                  </DialogHeader>
                  <DialogBody>
                    <Editor />
                  </DialogBody>
                  <DialogFooter gap={6} display="flex" flexDirection="column">
                    <Controls />
                    <Footer />
                  </DialogFooter>
                </DialogContent>
              </DialogPositioner>
            </Portal>
          </Dialog.Root>
        ) : (
          <Drawer.Root
            open={open}
            onOpenChange={handleOpenChange}
            placement="end"
            size="full"
          >
            <Portal>
              <DrawerBackdrop />
              <DrawerPositioner>
                <DrawerContent h="100svh" bg="white">
                  <DrawerHeader>
                    <Header />
                  </DrawerHeader>
                  <DrawerBody display="flex" alignItems="center">
                    <Editor />
                  </DrawerBody>
                  <DrawerFooter gap={6} display="flex" flexDirection="column">
                    <Controls />
                    <Footer />
                  </DrawerFooter>
                </DrawerContent>
              </DrawerPositioner>
            </Portal>
          </Drawer.Root>
        )}
      </Box>
    </CropperContext.Provider>
  );
}

export type { CropperProps as ImageEditorProps };
export { Cropper as ImageEditor };
